/**
 * Shared utility for formatting AI summary text.
 * Converts markdown to HTML and sanitizes for XSS prevention.
 */
import DOMPurify from 'dompurify';

function sanitizeHtml(html: string): string {
  if (!html) return '';
  // Use DOMPurify (allowlist) instead of a hand-rolled tag/attr blocklist.
  // The previous blocklist missed data: URLs, javascript: with leading
  // whitespace/control chars, and SVG/MathML vectors — and this renders stored
  // AI chat/summary content (SnapshotPage), so an injected payload must not
  // reach the DOM. Matches the house sink pattern used elsewhere.
  return DOMPurify.sanitize(html, { USE_PROFILES: { html: true } });
}

export function formatSummaryHtml(text: string): string {
  if (!text) return '';

  // Convert markdown bold (**text**) to <strong>
  let formatted = text.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');

  // Convert markdown lists and structure into HTML
  const lines = formatted.split('\n');
  const result: string[] = [];
  let inList = false;

  const headingPattern = /^<strong>(Key Patterns|Notable Examples|Examples|Summary|Findings|Overview|Conclusion)/i;

  for (const line of lines) {
    const trimmed = line.trim();
    const listMatch = trimmed.match(/^-\s+(.+)$/);

    if (listMatch && listMatch[1]) {
      const listContent = listMatch[1];

      if (headingPattern.test(listContent)) {
        if (inList) {
          result.push('</ul>');
          inList = false;
        }
        const headingMatch = listContent.match(/^(<strong>[^<]+<\/strong>):?/);
        if (headingMatch) {
          result.push(`<p class="mt-4 mb-1">${headingMatch[1]}</p>`);
        } else {
          result.push(`<p class="mt-4 mb-1">${listContent}</p>`);
        }
      } else {
        if (!inList) {
          result.push('<ul>');
          inList = true;
        }
        result.push(`<li>${listContent}</li>`);
      }
    } else if (trimmed) {
      if (inList) {
        result.push('</ul>');
        inList = false;
      }
      if (headingPattern.test(trimmed)) {
        result.push(`<p class="mt-4 mb-1">${trimmed}</p>`);
      } else {
        result.push(`<p>${trimmed}</p>`);
      }
    }
  }

  if (inList) {
    result.push('</ul>');
  }

  formatted = result.join('');

  return sanitizeHtml(formatted);
}
